import { useCallback, useMemo, type VoidFunctionComponent } from 'react';
import { DropdownButton, MenuItem } from 'react-bootstrap';
import { useDispatch } from 'react-redux';

import { useParams } from '../../../contexts/RouterContext';
import * as IssuesV2Store from '../../../redux/issuesV2';
import { formatTimestamp, trimString } from '../../../utils';
import Icon, { icons } from '../../generic/Icon';
import { useRevisionScanIdQueryParam } from '../hooks/useRevisionScanIdQueryParam';
import type { ProjectDashboardRevision, RevisionRouteParams } from '../projectDashboardTypes';

type Props = {
  revision: ProjectDashboardRevision;
};

const getScanButtonLabel = (scannedAt?: string) => (
  <span>
    <span className="full">Scanned: {scannedAt ? formatTimestamp(scannedAt) : 'Unknown'}</span>
    <span className="abbr">Scans</span>
  </span>
);

const ProjectSummaryIssueScanDropdown: VoidFunctionComponent<Props> = ({ revision }) => {
  const params = useParams<RevisionRouteParams>();
  const dispatch = useDispatch();
  const { revisionScanId, setRevisionScanId } = useRevisionScanIdQueryParam();

  const hasMultipleRevisionScans = (revision.data?.revisionScans?.length ?? 0) > 1;
  const shouldRenderDropdown = !!revisionScanId && hasMultipleRevisionScans;
  //  Note that this "lives" in Reflux, however updating the `revisionScanId` query param is what is actually behind the magic,
  //  as it gets synced way up in the RevisionContainer.
  const lastScannedAtTime = revision.data?.selectedRevisionScan?.scanned_at;

  const revisionScanItems = useMemo(() => {
    if (!revision.data?.revisionScans) {
      return [];
    }
    return revision.data.revisionScans.map(revScan => (
      <MenuItem
        active={revScan.id === revisionScanId}
        eventKey={revScan.id}
        key={revScan.id}
      >
        {formatTimestamp(revScan.scanned_at)}
      </MenuItem>
    ));
  }, [revisionScanId, revision.data?.revisionScans]);

  const onSelect = useCallback((nextRevisionScanId?: number) => {
    //  Handle setting the scope. Places where we `useCategorySummary` will smell an update to scope and re-fetch the category summary.
    if (nextRevisionScanId) {
      dispatch(IssuesV2Store.actions.setScanId(nextRevisionScanId));
      //  Add it to the URL in a query param.
      setRevisionScanId(nextRevisionScanId);
    }
  }, [dispatch, setRevisionScanId]);

  return (
    <b className="inline-header">
      Issues in {trimString(params.reference, 15)}
      {shouldRenderDropdown && (
        <>
          <Icon icon={icons.angleRight} />
          <DropdownButton
            id="project-summary-issue-scan-dropdown"
            className="dropdown-mid choose-scan inline-header"
            title={getScanButtonLabel(lastScannedAtTime)}
            onSelect={onSelect}
          >
            {revisionScanItems}
          </DropdownButton>
        </>
      )}
    </b>
  );
};
export default ProjectSummaryIssueScanDropdown;
